// ImbTable.tsx
// Copyright (c) 2020 Neomantra Corp

import React, { useState, useEffect } from 'react'
import numeral from 'numeral'
import moment from 'moment'
import BaseTable, { AutoResizer, Column, SortOrder } from 'react-base-table'
import 'react-base-table/styles.css'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/rootReducer'

interface Imbalance {
    timeval: number
    timestampStr: string
    imbalance_shares: number
    paired_shares: number
    ref_px: number
    near_px: number
    far_px: number
    update_num: number
}

interface ImbTableProps {
    index: any
}

const stringRenderer = ({ className, cellData }) => <div className={className}>{cellData}</div>
const momentRenderer = ({ className, cellData }) => <div className={className}>{moment(cellData * 1000).format('h:mm:ss.SSS')}</div>
const numberRenderer0 = ({ className, cellData, rowData }) => <div className={className}>{numeral(cellData).format('0,0')}</div>
const numberRenderer1 = ({ className, cellData, rowData }) => (
    <div style={cellData === 0 ? { color: 'black' } : cellData > 0 ? { color: 'green' } : { color: 'red' }} className={className}>
        {numeral(cellData).format('0,0')}
    </div>
)
const numberRenderer2 = ({ className, cellData, rowData }) => <div className={className}>{numeral(cellData).format('0,0.00')}</div>

const columnRenderers = {
    time: momentRenderer,
    ticker: stringRenderer,
    paired_shares: numberRenderer0,
    imb_shares: numberRenderer1,
    imb_dollars: numberRenderer1,
    ref_price: numberRenderer2,
    near_price: numberRenderer2,
    far_price: numberRenderer2,
    update_num: numberRenderer0,
}

const ImbTableCell = (cellProps) => {
    const renderer = columnRenderers[cellProps.column.key] || stringRenderer
    return renderer(cellProps)
}

const imbDollarsGetter = ({ rowData }) => {
    return rowData.i * rowData.r
}

const imbTimeWithMicroGetter = ({ rowData }) => {
    return rowData.t + (rowData.m === 'Q' ? 14400 : 0) + rowData.u / 1000000
}

const TickerCardTable = ({ index }: ImbTableProps) => {
    const cardStack = useSelector((state: RootState) => state.console.cardStack),
        data = cardStack[index] && cardStack[index].data,
        [dataState, setDataState] = useState(data)

    const defaultSort = { key: 'time', order: SortOrder.DESC }
    const [sortBy, setSortBy] = useState(defaultSort)

    const onColumnSort = (sortBy: any) => {
        setSortBy(sortBy)
        let revData = [...dataState]
        setDataState(revData.reverse())
    }

    useEffect(() => {
        setSortBy(defaultSort)
        setDataState(data)
    }, [data])

    return (
        <AutoResizer>
            {({ width, height }) => (
                <BaseTable
                    sortBy={sortBy}
                    onColumnSort={onColumnSort}
                    width={width}
                    height={height - 15}
                    data={dataState}
                    headerHeight={28}
                    rowHeight={28}
                    rowKey={'s'}
                    components={{ TableCell: ImbTableCell }}
                    style={{ fontFamily: 'IBM Plex Mono, Courier, monospace', fontSize: '12px' }}
                >
                    <Column sortable={true} title='Time' key='time' dataGetter={imbTimeWithMicroGetter} width={115} align='left' />
                    <Column title='Paired' key='paired_shares' dataKey='p' width={95} align='right' />
                    <Column title='Imb' key='imb_shares' dataKey='i' width={95} align='right' />
                    <Column title='Imb $$' key='imb_dollars' dataGetter={imbDollarsGetter} width={105} align='right' />
                    <Column title='Ref Px' key='ref_price' dataKey='r' width={80} align='right' />
                    <Column title='Near Px' key='near_price' dataKey='n' width={80} align='right' />
                    <Column title='Far Px' key='far_price' dataKey='f' width={80} align='right' />
                </BaseTable>
            )}
        </AutoResizer>
    )
}

export default TickerCardTable
